/**
 * lazyWithRetry — a lazily loaded page that never reloads the whole screen, and
 * draws at once when its code is already here (REVIEW-2, Design System 2.4).
 *
 * Michael, 2026-10-29: pages "refresh" and the rail "goes away". Two causes,
 * both fixed here:
 *
 * 1. A tab opened before a deploy asks for the old code file; Railway serves only
 *    the new build, so the file 404s. This used to `window.location.reload()` —
 *    the blink. Now: when a new build is live (`appVersion`), the page shows
 *    `NewVersionPage` and the shell offers "A new version of the app is ready —
 *    Refresh"; anything else gets one quiet retry, then `PageLoadFailedPage`.
 *    Never a silent reload.
 * 2. The first visit to a page waited for its code behind a blank spinner. Every
 *    page registers its loader here; the shell calls `preloadLazyPages()` once
 *    the app is idle, so the code is already in the tab before the click. A page
 *    whose code is loaded renders SYNCHRONOUSLY — no Suspense, no spinner — so
 *    the page appears the moment you click.
 *
 * Still a drop-in: `lazyWithRetry(() => import('./X'))` returns a component
 * typed as the page itself.
 */
import React, { ComponentType, lazy } from 'react';
import { announceNewVersion, isNewVersionLive } from './appVersion';
import { NewVersionPage, PageLoadFailedPage } from '../components/shared/LazyPageFallbacks';

type Mod<T> = { default: T };

export function isChunkLoadError(err: unknown): boolean {
  const e = err as { message?: string; name?: string } | null;
  const msg = (e && e.message) || '';
  return (
    msg.includes('Loading chunk') ||
    msg.includes('Loading CSS chunk') ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Failed to load module script') ||
    msg.includes('ChunkLoadError') ||
    (!!e && e.name === 'ChunkLoadError')
  );
}

const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

// ── the preload registry ──────────────────────────────────────────────────
// Every lazy page adds its loader; once the shell arms preloading, pending
// loaders run when the browser is idle, a few at a time, so they never compete
// with the page the person is looking at. A page registered later (a lazy page
// inside another lazy page) is picked up on the next idle slot.

const pending = new Set<() => Promise<unknown>>();
let armed = false;
let scheduled = false;

type IdleWindow = Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number };

function onIdle(fn: () => void): void {
  const w = window as IdleWindow;
  if (typeof w.requestIdleCallback === 'function') w.requestIdleCallback(fn, { timeout: 4000 });
  else setTimeout(fn, 1200);
}

function saveData(): boolean {
  const c = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
  return !!c && (c.saveData === true || c.effectiveType === 'slow-2g' || c.effectiveType === '2g');
}

async function drain(): Promise<void> {
  const batch = Array.from(pending);
  pending.clear();
  // Three at a time: steady, never a burst that slows the page in front.
  for (let i = 0; i < batch.length; i += 3) {
    await Promise.all(batch.slice(i, i + 3).map((load) => load().catch(() => undefined)));
  }
}

function schedule(): void {
  if (!armed || scheduled || pending.size === 0 || typeof window === 'undefined') return;
  scheduled = true;
  onIdle(() => {
    scheduled = false;
    void drain().then(() => schedule());
  });
}

/** Arm background preloading of every registered page (the shell calls this once). */
export function preloadLazyPages(): void {
  if (armed || typeof window === 'undefined' || saveData()) return;
  armed = true;
  schedule();
}

/** Test seam. */
export function resetLazyPreload(): void {
  pending.clear();
  armed = false;
  scheduled = false;
}

// ── the loader ────────────────────────────────────────────────────────────

/** A page's code failed in front of the person: never reload, always say what happened. */
async function recoverInForeground<T>(factory: () => Promise<Mod<T>>, err: unknown): Promise<Mod<T>> {
  if (!isChunkLoadError(err)) throw err;
  if (await isNewVersionLive()) {
    announceNewVersion();
    return { default: NewVersionPage as unknown as T };
  }
  // Not a new build: most likely a blip. One quiet retry, then the honest error.
  await wait(1500);
  try {
    return await factory();
  } catch (again) {
    if (!isChunkLoadError(again)) throw again;
    return { default: PageLoadFailedPage as unknown as T };
  }
}

export function lazyWithRetry<T extends ComponentType<any>>(factory: () => Promise<Mod<T>>): T {
  let loaded: Mod<T> | null = null;
  let inflight: Promise<Mod<T>> | null = null;

  const load = (): Promise<Mod<T>> => {
    if (loaded) return Promise.resolve(loaded);
    if (!inflight) {
      inflight = factory().then(
        (m) => { loaded = m; return m; },
        (err) => { inflight = null; throw err; },
      );
    }
    return inflight;
  };

  // Background: a failure is left for the click to handle (it may be a blip),
  // except that a live new build is worth telling the person about now.
  pending.add(() => load().catch(async (err) => {
    if (isChunkLoadError(err) && await isNewVersionLive()) announceNewVersion();
  }));
  schedule();

  const Lazy = lazy(() => load().catch((err) => recoverInForeground(factory, err)));

  // Loaded already (preloaded, or visited before): draw it now, no Suspense.
  // The choice is made once per mount and kept: switching a mounted page from
  // the lazy wrapper to the real component would change its type and remount it.
  const Page = React.forwardRef<unknown, Record<string, unknown>>((props, ref) => {
    const direct = React.useRef(loaded !== null).current;
    const C = (direct && loaded ? loaded.default : Lazy) as unknown as React.ComponentType<Record<string, unknown> & { ref?: React.Ref<unknown> }>;
    return <C ref={ref} {...props} />;
  });
  Page.displayName = 'LazyPage';
  return Page as unknown as T;
}
